import React, { useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import COPY from '../../connect/finance/connect-finance-copy';

dayjs.extend(utc);

// Weekends are not banking days, so "+5 business days" is not "+5 days" —
// the difference is two days on most payouts and the whole point of the
// sentence is a date the restaurant can hold a bank to.
export const addBusinessDays = (value, count) => {
  let date = dayjs.utc(value);
  let left = count;
  while (left > 0) {
    date = date.add(1, 'day');
    const weekday = date.day();
    if (weekday !== 0 && weekday !== 6) left -= 1;
  }
  return date;
};

// "Thisway" is bold on screen, but the sentence lives in COPY as one string
// (spec 2026-09-22 §10) — splitting it here keeps the copy table readable
// and stops two halves of a sentence drifting apart.
const withBoldThisway = (text) => {
  const [before, after] = text.split('Thisway');
  if (after === undefined) return text;
  return (
    <>
      {before}
      <b className="font-bold text-fin-ink">Thisway</b>
      {after}
    </>
  );
};

// Frame 2c's right panel. The trace ID is Stripe's own and it only exists
// once the payout reaches `paid` — before that the panel stops after the
// second sentence and draws no field (mock 2c DEV NOTE).
const PayoutStatementHelp = ({ detail }) => {
  const copy = COPY.restaurant;
  const d = detail || {};
  const [copied, setCopied] = useState(false);
  // The Trace ID field is drawn only alongside the sentence that explains
  // it, so a payout with no deadline yet must never show the field either
  // (spec §6 couples the field and its sentence).
  const showTrace = d.status === 'paid' && !!d.traceId && !!d.arrivalDate;
  const deadline = showTrace
    ? addBusinessDays(d.arrivalDate, 5).format('MMM D')
    : null;

  const copyTrace = async () => {
    try {
      await navigator.clipboard.writeText(d.traceId);
      setCopied(true);
    } catch {
      // A clipboard the browser refuses is not worth an error state: the id
      // is on screen and selectable either way.
      setCopied(false);
    }
  };

  return (
    <aside className="flex flex-col gap-3.5 border-t border-fin-border bg-fin-recess px-7 py-6 lg:border-l lg:border-t-0">
      <h3 className="text-[14px] font-bold text-fin-ink">Can&apos;t find this payout on your statement?</h3>
      <p className="text-[13px] leading-[1.55] text-fin-muted-2">
        {withBoldThisway(copy.statementHelp1)}
        {deadline ? <> {copy.statementHelp2(deadline)}</> : null}
      </p>
      {showTrace ? (
        <div>
          <p className="text-[12.5px] text-fin-muted">Trace ID</p>
          <div className="mt-1.5 flex items-center gap-1.5 rounded-[7px] border border-fin-border bg-fin-surface py-[7px] pl-2.5 pr-2">
            <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-fin-ink-2">{d.traceId}</span>
            <button
              type="button"
              aria-label="Copy trace ID"
              onClick={copyTrace}
              className="inline-flex p-0.5 text-fin-muted hover:text-fin-ink"
            >
              <ClipboardDocumentIcon className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
          {copied ? <p className="mt-1 text-[12px] text-fin-green">Copied</p> : null}
        </div>
      ) : null}
    </aside>
  );
};

export default PayoutStatementHelp;
